/**
 * SettingsContext loads /settings once after login and exposes a typed
 * read accessor. Defaults are inlined so a missing key (network error,
 * first-deploy edge case) never crashes the UI — they mirror the server
 * schema's default for each key.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/api/v1";
const AUTH_STORAGE_KEY = "occ.session.v1";
const REFRESH_MS = 60_000;

type SettingsMap = Record<string, unknown>;

interface SettingsCtx {
  settings: SettingsMap;
  ready: boolean;
  reload: () => Promise<void>;
}

const SettingsContext = createContext<SettingsCtx>({
  settings: {},
  ready: false,
  reload: async () => {},
});

function readToken(): string | null {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as { accessToken?: string }).accessToken ?? null;
  } catch { return null; }
}

const DEFAULTS: SettingsMap = {
  "occ.fleet_stream_interval_ms":   2000,
  "occ.alerts_stream_interval_ms":  3000,
  "occ.sse_reconnect_ms":           5000,
  "occ.fleet_fallback_poll_ms":     3000,
  "occ.alerts_fallback_poll_ms":    5000,
  "occ.trips_refresh_ms":           5000,
  "occ.panic_refresh_ms":           5000,
  "occ.alerts_refresh_ms":         10000,
  "occ.incidents_resolved_window_ms": 3_600_000,
  "occ.late_warn_seconds":           60,
  "occ.late_crit_seconds":          300,
  "occ.map_default_lat":         33.6844,
  "occ.map_default_lon":         73.0479,
  "occ.map_default_zoom":              12,
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<SettingsMap>(DEFAULTS);
  const [ready, setReady] = useState<boolean>(false);
  const inflight = useRef<boolean>(false);

  const reload = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const token = readToken();
      if (!token) {
        setReady(true);
        return;
      }
      const res = await fetch(`${API_BASE}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const fetched = (await res.json()) as SettingsMap;
        setSettings({ ...DEFAULTS, ...fetched });
      }
    } catch {
      // Fall through to defaults.
    } finally {
      setReady(true);
      inflight.current = false;
    }
  }, []);

  useEffect(() => {
    reload();
    const id = window.setInterval(reload, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [reload]);

  const value = useMemo(() => ({ settings, ready, reload }), [settings, ready, reload]);
  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

/** Generic accessor with default fallback. */
export function useSetting<T>(key: keyof typeof DEFAULTS | string, fallback: T): T {
  const { settings } = useContext(SettingsContext);
  const v = settings[key];
  return (v === undefined || v === null ? fallback : v) as T;
}

export function useSettings(): SettingsMap {
  return useContext(SettingsContext).settings;
}
